/**
 * sportsTaxonomy — single, trip-independent source of truth for the club's
 * competitive SPORTS, DISCIPLINES and COMPETITION types.
 *
 * Shared by: CMS Championship form, Operations Championship roster,
 * Members → Athlete Card, public championship labels, athlete eligibility,
 * filters and exports. Do NOT duplicate these arrays in components — import
 * from here (or via the backward-compatible re-export in tripsTaxonomy.js).
 *
 * Values are stored as stable internal keys; bilingual labels are resolved at
 * render time only.
 */

export const CHAMPIONSHIP_SPORTS = [
  { key: 'sport_climbing',        en: 'Sport Climbing',            gr: 'Αγωνιστική Αναρρίχηση' },
  { key: 'mountain_trail_running',en: 'Mountain & Trail Running',  gr: 'Ορεινό και Trail Τρέξιμο' },
  { key: 'mountain_bike',         en: 'Mountain Bike',             gr: 'Ορεινή Ποδηλασία' },
];

export const CHAMPIONSHIP_DISCIPLINES = {
  sport_climbing: [
    { key: 'lead',                  en: 'Lead',                    gr: 'Αναρρίχηση Δυσκολίας' },
    { key: 'boulder',               en: 'Boulder',                 gr: 'Αναρρίχηση Boulder' },
    { key: 'speed',                 en: 'Speed',                   gr: 'Αναρρίχηση Ταχύτητας' },
    { key: 'boulder_lead_combined', en: 'Boulder & Lead Combined', gr: 'Συνδυαστικό Boulder & Lead' },
    { key: 'rock_climbing',         en: 'Rock Climbing',           gr: 'Αναρρίχηση Βράχου' },
  ],
  mountain_trail_running: [
    { key: 'classic_uphill',  en: 'Classic Uphill',    gr: 'Ορεινή Ανάβαση' },
    { key: 'classic_up_down', en: 'Classic Up & Down', gr: 'Ορεινός Αγώνας Ανάβασης–Κατάβασης' },
    { key: 'trail_running',   en: 'Trail Running',     gr: 'Trail Running' },
    { key: 'long_trail',      en: 'Long Trail',        gr: 'Long Trail' },
    { key: 'ultra_trail',     en: 'Ultra Trail',       gr: 'Ultra Trail' },
  ],
  mountain_bike: [
    { key: 'xco', en: 'Cross-Country Olympic (XCO)',        gr: 'Ολυμπιακό Cross-Country' },
    { key: 'xcc', en: 'Cross-Country Short Track (XCC)',    gr: 'Cross-Country Μικρής Διαδρομής' },
    { key: 'xcm', en: 'Cross-Country Marathon (XCM)',       gr: 'Μαραθώνιος Ορεινής Ποδηλασίας' },
    { key: 'xcr', en: 'Cross-Country Team Relay (XCR)',     gr: 'Ομαδική Σκυταλοδρομία Cross-Country' },
    { key: 'xce', en: 'Cross-Country Eliminator (XCE)',     gr: 'Cross-Country Αποκλεισμού' },
    { key: 'dhi', en: 'Downhill Individual (DHI)',          gr: 'Ατομική Κατάβαση' },
    { key: 'edr', en: 'Enduro (EDR)',                       gr: 'Enduro' },
    { key: 'e_xc',en: 'E-Mountain Bike Cross-Country (E-XC)',gr: 'Ηλεκτρικό Cross-Country MTB' },
  ],
};

export const COMPETITION_TYPES = [
  { key: 'national',   en: 'National Championship',    gr: 'Πανελλήνιο Πρωτάθλημα' },
  { key: 'cup',        en: 'Cup',                       gr: 'Κύπελλο' },
  { key: 'interclub',  en: 'Interclub Competition',     gr: 'Διασυλλογικός Αγώνας' },
  { key: 'international',en: 'International Competition', gr: 'Διεθνής Αγώνας' },
  { key: 'open',       en: 'Open Competition',          gr: 'Ανοικτός Αγώνας' },
  { key: 'other',      en: 'Other',                     gr: 'Άλλο' },
];

// Resolve a stored sport/discipline/competition_type value (stable key OR
// legacy English label) to its official bilingual label. Prevents raw internal
// keys / English strings leaking onto the public page.
const _flatDisciplines = Object.values(CHAMPIONSHIP_DISCIPLINES).flat();
const _matchLabel = (list, value, lang) => {
  if (!value) return '';
  const v = String(value).trim().toLowerCase();
  const hit = list.find((o) => o.key.toLowerCase() === v || o.en.toLowerCase() === v || o.gr.toLowerCase() === v);
  return hit ? (lang === 'gr' ? hit.gr : hit.en) : String(value);
};
export const tChampionshipSport = (value, lang) => _matchLabel(CHAMPIONSHIP_SPORTS, value, lang);
export const tChampionshipDiscipline = (value, lang) => _matchLabel(_flatDisciplines, value, lang);
export const tCompetitionType = (value, lang) => _matchLabel(COMPETITION_TYPES, value, lang);

// ─────────────────────────────────────────────────────────────────────────
// Legacy athlete-profile coercion (DISPLAY-ONLY).
//
// The old Athlete Card used its own tiny lists: sport key `rock_climbing` and
// disciplines lead/boulder/speed/multi/rock. We fold these onto the shared
// taxonomy *for display* so the dependent dropdowns pre-select the right entry
// — but the DB is never mass-rewritten. When the admin saves, the canonical
// internal key currently selected is what gets persisted.
//
// Confirmed-safe mappings (old label matched the new label exactly):
//   sport      rock_climbing → sport_climbing   (both = «Αγωνιστική/Αναρρίχηση»)
//   discipline rock          → rock_climbing     (both = «Αναρρίχηση Βράχου»)
// Ambiguous values (e.g. `multi` = "many disciplines", NOT the official
// combined event) are NOT auto-mapped: they surface a "needs update" flag so
// the admin picks a valid discipline before saving.
// ─────────────────────────────────────────────────────────────────────────
const LEGACY_ATHLETE_SPORT = { rock_climbing: 'sport_climbing' };
const LEGACY_ATHLETE_DISCIPLINE = { rock: 'rock_climbing' };
const LEGACY_DISCIPLINE_LABELS = {
  multi: { en: 'Multiple', gr: 'Πολλαπλά' },
};

/** Returns { key, needsUpdate, raw } for an athlete's stored sport value. */
export const coerceAthleteSport = (value) => {
  if (!value) return { key: '', needsUpdate: false, raw: '' };
  const mapped = LEGACY_ATHLETE_SPORT[value] || value;
  const known = CHAMPIONSHIP_SPORTS.some((s) => s.key === mapped);
  return { key: known ? mapped : '', needsUpdate: !known, raw: value };
};

/** Returns { key, needsUpdate, raw } for a discipline under a given sport. */
export const coerceAthleteDiscipline = (sportKey, value) => {
  if (!value) return { key: '', needsUpdate: false, raw: '' };
  const mapped = LEGACY_ATHLETE_DISCIPLINE[value] || value;
  const list = CHAMPIONSHIP_DISCIPLINES[sportKey] || [];
  const known = list.some((d) => d.key === mapped);
  return { key: known ? mapped : '', needsUpdate: !known, raw: value };
};

/** Human label for an unrecognised legacy discipline (for the warning badge). */
export const legacyDisciplineLabel = (value, lang) => {
  const e = LEGACY_DISCIPLINE_LABELS[value];
  if (e) return lang === 'gr' ? e.gr : e.en;
  return value || '';
};
